import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../auth/services/auth.service';
import { environment } from 'src/environments/environment';
import { logSessionEvent, SessionEventReason } from '../auth/session-log';
import { withAuthHeaders } from './auth-headers';

// Wraps whatever refreshToken() throws so the second catchError in
// handle401() can tell "the refresh itself failed" apart from "the refresh
// succeeded but the retried request failed for its own reason" — only the
// former is grounds for logout (design D5/D3).
class RefreshFailedError extends Error {
    constructor(readonly refreshError: unknown) {
        super();
    }
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    constructor(private authService: AuthService) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Captured at DISPATCH time, before next.handle() runs — capturing it
        // later (e.g. on 401 arrival) would read an already-bumped epoch for a
        // straggling request and produce a false "unchanged" match (design D3).
        const epochAtRequest = this.authService.sessionEpoch;

        return next.handle(request).pipe(catchError(err => {
            const isApiUrl = request.url.startsWith(environment.apiUrl);

            if (err.status === 401 && isApiUrl && !this.isRefreshEndpoint(request.url) && this.authService.hasToken) {
                // Gate BEFORE routing into the refresh-and-retry flow: if the
                // epoch has already moved on since this request was
                // dispatched (e.g. a new login happened while this 401 was
                // in flight), refreshToken() would resolve at the NEW
                // session's epoch and hand back a valid fresh token for the
                // wrong identity, and the retry would replay this stale
                // request under it — a cross-session write if it's a
                // mutation. Discard it here instead: no refresh, no retry.
                if (epochAtRequest !== this.authService.sessionEpoch) {
                    this.logEvent('interceptor.skip.superseded', epochAtRequest, request.url, err.status);
                    return throwError(() => this.toAppError(err));
                }

                return this.handle401(request, next, err, epochAtRequest);
            }

            if (err.status === 403 && isApiUrl) {
                // 403 means "forbidden for this action" (e.g. CSRF on
                // POST /push/devices), NOT "session invalid" — the backend
                // answers expired/revoked sessions with 401. Never log out
                // here; just leave a breadcrumb and let the caller handle it.
                this.logEvent('interceptor.skip.forbidden', epochAtRequest, request.url, err.status);
                return throwError(() => this.toAppError(err));
            }

            if (err.status === 401) {
                // auto logout if 401 response returned from api — but only
                // when it belongs to the session that is still active; a
                // straggling failure from a superseded session must not
                // terminate the current one (design: direct path is also
                // epoch-gated).
                if (epochAtRequest === this.authService.sessionEpoch) {
                    this.logEvent('interceptor.logout.direct', epochAtRequest, request.url, err.status);
                    this.authService.logout();
                } else {
                    this.logEvent('interceptor.skip.superseded', epochAtRequest, request.url, err.status);
                }
            }

            return throwError(() => this.toAppError(err));
        }))
    }

    // Dedupe of concurrent refreshes is entirely AuthService's responsibility
    // (single shared in-flight observable, see auth.service.ts). This
    // interceptor no longer tracks its own isRefreshing flag or queues
    // followers on a local subject — every 401 simply calls
    // AuthService.refreshToken() and lets it decide whether that's a new
    // request or a reused in-flight one.
    private handle401(request: HttpRequest<any>, next: HttpHandler, originalErr: any, epochAtRequest: number): Observable<HttpEvent<any>> {
        return this.authService.refreshToken().pipe(
            catchError(e => throwError(() => new RefreshFailedError(e))),
            switchMap(token => next.handle(this.addToken(request, token))),
            catchError(err => {
                if (err instanceof RefreshFailedError) {
                    if (epochAtRequest === this.authService.sessionEpoch) {
                        this.logEvent('interceptor.logout.refresh-failed', epochAtRequest, request.url, originalErr?.status);
                        this.authService.logout();
                    } else {
                        this.logEvent('interceptor.skip.superseded', epochAtRequest, request.url, originalErr?.status);
                    }
                    return throwError(() => this.toAppError(originalErr));
                }

                // The refresh itself succeeded — this is the RETRIED request's own
                // failure (e.g. a 500), unrelated to auth. It must propagate as-is
                // and must never trigger a logout (design: this is the fix's core
                // behavior change — previously originalErr, the 401, leaked here).
                this.logEvent('interceptor.skip.retry-failed', epochAtRequest, request.url, err.status);
                return throwError(() => this.toAppError(err));
            })
        );
    }

    private logEvent(reason: SessionEventReason, requestEpoch: number, url: string, status?: number): void {
        logSessionEvent({
            reason,
            at: new Date().toISOString(),
            epoch: this.authService.sessionEpoch,
            requestEpoch,
            path: this.pathOf(url),
            status
        });
    }

    private pathOf(url: string): string {
        return url.split('?')[0];
    }

    private isRefreshEndpoint(url: string): boolean {
        return url.includes('/auth/token-refresh');
    }

    // Re-applies the FULL header set, not just Authorization: the refresh
    // response ROTATED the xsrf token, and this retry does not pass back through
    // JwtInterceptor (registration order — see auth-headers.ts). handle401 has
    // already gated on isApiUrl.
    private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
        return withAuthHeaders(request, { token, xsrfToken: this.authService.xsrfToken });
    }

    private toAppError(err: any) {
        const errorBody = err.error || err.statusText;
        const error = typeof errorBody === 'object' && errorBody !== null
            ? { ...errorBody, status: err.status }
            : errorBody;
        console.error(err);
        return error;
    }
}
